import React, { useState, useEffect, useRef, FC, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import useModalPhoneItemState from "@/widgets/modalPhoneItem/libs/hook/useModalPhoneItemState";
import useModalPhoneItemEffects from "@/widgets/modalPhoneItem/libs/hook/useModalPhoneItemEffects";
import useModalPhoneItemControls from "@/widgets/modalPhoneItem/libs/hook/useModalPhoneItemControls";
import Header from "@/widgets/headerModal/ui/Header/Header";
import ButtonPay from "@/widgets/paybackButton/ui/ButtonPlay/ButtonPay";
import { VideoControls } from "@/widgets/videoControls/ui/VideoControls/VideoControls";
import PhotoCardList from "@/widgets/photoCardList/ui/PhotoCardList/PhotoCardList";
import AdditionalInfo from "@/widgets/additionalInfo/ui/AdditionalInfo/AdditionalInfo";
import SavePosition from "@/widgets/savePosition/ui/SavePosition/SavePosition";
import {
  useGetHiddenAppsQuery,
  useGetUserQuery,
  useGetAppVideoFlowsQuery,
} from "@/features/auth/api/authAPI";
import {
  getModalMenuPhoneState,
  modalMenuPhoneActions,
} from "@/entities/modalMenuPhone";
import { videoActions } from "@/entities/video/model/slice/videoSlice";
import ErrorModalPhoneItem from "@/shared/ui/ErrorModalPhoneItem/ErrorModalPhoneItem";
import ButtonFilter from "@/shared/ui/ButtonFilter/ButtonFilter";
import AppStoreScreensModal from "@/shared/ui/AppStoreScreensModal/AppStoreScreensModal";
import { secondsToHMS } from "@/widgets/modalPhoneItem/libs/const/secondsToHMS";
import {
  buttonData,
  webOnboardingData,
} from "@/widgets/modalPhoneItem/libs/data/buttonData";
import styles from "@/widgets/modalPhoneItem/ui/ModalPhoneItem/ModalPhoneItem.module.scss";
import { useMe, useCurrentOrganization } from "@/Hooks";
import mixpanel from "@/shared/lib/mixpanel";
import { Flows } from "@/shared/ui/Flows/Flows";
import Spinner from "@/shared/ui/Spinner/Spinner";
import { toast } from "react-toastify";
import { getVideoState } from "@/entities/video/model/selectors/getVideoState";
import WebOnboardingVideoPage from "@/widgets/WebOnboardingVideoPage/WebOnboardingVideoPage";
import Phone from "@/widgets/phone/ui/Phone/Phone";
import {
  useSelectedAppStoreScreens,
  useSelectedOnboardingScreens,
  useSelectedScreens,
} from "@/entities/selectedScreens";
import SelectedScreensPopup from "../Virtual/SelectedScreensPopup/SelectedScreensPopup";
import { useOpenProModal } from "@/Hooks/useOpenBillingModal";
import { useWindowWidth } from "@/Hooks/useWindowWidth";
import VersionsMenu from "@/widgets/headerModal/ui/Header/VersionsMenu";

interface ModalMenuItemProps {}

const AppVideoView: FC<ModalMenuItemProps> = () => {
  const { isActiveAnyModal, setIsActiveAnyModal } = useOutletContext();

  const phoneRef = useRef<HTMLDivElement | null>(null);
  const [showSavedMode, setShowSavedMode] = useState(false);
  const [showActiveIcon, setShowActiveIcon] = useState(true);
  const [tempText, setTempText] = useState<string>("");
  const [isActivePoster, setIsActivePoster] = useState<boolean>(true);
  const [showSaveApp, setShowSaveApp] = useState(false);
  const currentOrganization = useCurrentOrganization();
  const token = localStorage.getItem("access_token");
  const {
    state,
    setShowLottie,
    setHasNullItem,
    setVideoReady,
    setImagesReady,
    setIsModalOpen,
    setIsTimestampReady,
    setIsScrolling,
    setMarkers,
    photoCardListRef,
    setCroppedImages,
  } = useModalPhoneItemState();

  const { data: appDataHidden } = useGetHiddenAppsQuery("-created_at", {
    skip: !token,
  });
  const {
    ref,
    refLottie,
    inView,
    inViewLottie,
    apiDataVideosOne,
    apiDataVideosOneOB,
    isLoading,
    isLoadingOB,
    appDataOne,
    apiDataVideosScreensById,
    apiDataVideosStoreScreens,
    appStoreImageId,
    appId,
    refetchDataVideo,
    refetchDataVideoOB,
    apiDataVideosLabelsById,
    apiDataPointsByVideo,
    refetchDataScreens,
    refetchDataStore,
    isErrorDataVideosOne,
    isErrorDataVideosOneOB,
    isErrorAppDataOne,
  } = useModalPhoneItemEffects(state, {
    setIsScrolling,
    setHasNullItem,
    photoCardListRef,
    setShowLottie,
    setCroppedImages,
  });
  const { data: userData } = useGetUserQuery(null, { skip: !token });

  const { videoRef, onSeek, openModal, closeModal } = useModalPhoneItemControls(
    setVideoReady,
    setIsModalOpen,
    refetchDataVideo,
  );

  const [appVideoScreens, setAppVideoScreens] = useState([]);
  const [savedPoints, setSavedPoints] = useState([]);
  const [appStoreScreens, setAppStoreScreens] = useState([]);
  const { me } = useMe();

  const { data: flowsData } = useGetAppVideoFlowsQuery(
    Number(apiDataVideosOne?.id),
    {
      skip: !appDataOne || !apiDataVideosOne?.id,
    },
  );

  const openProModal = useOpenProModal();

  const handleOpenBillingPage = () => {
    openProModal("App Video View");
  };

  useEffect(() => {
    if (apiDataVideosScreensById) {
      setAppVideoScreens(apiDataVideosScreensById.results);
    }
  }, [apiDataVideosScreensById]);

  useEffect(() => {
    if (apiDataPointsByVideo) {
      setSavedPoints(apiDataPointsByVideo.results);
    }
  }, [apiDataPointsByVideo]);

  useEffect(() => {
    if (apiDataVideosStoreScreens) {
      setAppStoreScreens(apiDataVideosStoreScreens.results);
    }
  }, [apiDataVideosStoreScreens]);

  const handleOverlayClick = (e: MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.target === e.currentTarget) {
      closeModal();
    }
  };

  const dispatch = useDispatch();
  const { activeIndex, isModalOpenPhotoCard } = useSelector(
    getModalMenuPhoneState,
  );
  const href = window.location.href;
  const [apiDataVideosOneActive, setApiDataVideosOneActive] =
    useState<boolean>(false);

  const isHidden = appDataHidden?.results.some(
    (item) => Number(item.id) === Number(appId),
  );
  const hiddenAppItem = appDataHidden?.results.find(
    (item) => Number(item.id) === Number(appId),
  );

  useEffect(() => {
    console.log("isModalOpenPhotoCard", isModalOpenPhotoCard);
    setIsActiveAnyModal(isModalOpenPhotoCard);
  }, [isModalOpenPhotoCard]);

  const iconTimerSaveAppRef = useRef<NodeJS.Timeout | null>(null);

  const iconTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hideTimerRef = useRef<NodeJS.Timeout | null>(null);
  const activeFlowRef = useRef<HTMLDivElement | null>(null);

  const handleSavePointClick = () => {
    setShowSavedMode(true);
    setShowActiveIcon(true);

    iconTimerRef.current = setTimeout(() => {
      setVideoReady(true);
      setShowActiveIcon(false);
    }, 1000);

    hideTimerRef.current = setTimeout(() => {
      setShowSavedMode(false);
    }, 10000);
  };

  const { videoType } = useSelector(getVideoState);
  const currentVideoData =
    videoType === 1 ? apiDataVideosOne : apiDataVideosOneOB;
  const isCurrentVideoLoading = videoType === 1 ? isLoading : isLoadingOB;

  useEffect(() => {
    if (apiDataVideosOneOB && videoType === 1 && !apiDataVideosOne) {
      dispatch(videoActions.setVideoType(2));
    }
  }, [apiDataVideosOne, apiDataVideosOneOB]);

  if (!currentVideoData || (!state.showLottie && state.videoReady)) {
    setTimeout(() => {
      setApiDataVideosOneActive(true);
    }, 1000);
  }

  useEffect(() => {
    if (currentVideoData && !state.videoReady) {
      if (videoType === 1) {
        refetchDataVideo();
      } else {
        refetchDataVideoOB();
      }
    }
  }, [
    currentVideoData,
    state.videoReady,
    refetchDataVideo,
    refetchDataVideoOB,
  ]);

  const handleVideoTypeChange = (type: number) => {
    dispatch(videoActions.setVideoType(type));
  };

  const storeScreens = useSelectedAppStoreScreens();
  const appReplayScreens = useSelectedScreens();
  const webOnboardingScreens = useSelectedOnboardingScreens();

  const getSelectedScreensData = () => {
    // If we're in App Store screens tab
    if (activeIndex === 1) {
      return storeScreens;
    }
    // If we're in the main tab but viewing web onboarding
    else if (activeIndex === 0 && videoType === 2) {
      return webOnboardingScreens;
    }
    // Default to app replays (main tab, app replay video)
    else {
      return appReplayScreens;
    }
  };

  // Get the appropriate selected screens based on the current mode
  const { selectedScreens } = getSelectedScreensData();

  // Helper function to get the current screen type name for the popup
  const getScreenTypeForPopup = () => {
    if (activeIndex === 1) {
      return "appStoreScreens";
    } else if (activeIndex === 0 && videoType === 2) {
      return "onboardingScreens";
    } else {
      return "appReplays";
    }
  };

  // Add this new state to track saved/unsaved screens
  const [savedScreensMap, setSavedScreensMap] = useState<
    Record<number, { is_saved: boolean; is_saved_pk: number | null }>
  >({});

  // Add this function to update saved status of screens
  const updateScreensSavedStatus = useCallback(
    (
      screenIds: number[],
      isSaved: boolean,
      savedPointIds: Record<number, number> | null,
    ) => {
      setSavedScreensMap((prevMap) => {
        const newMap = { ...prevMap };

        screenIds.forEach((screenId) => {
          if (isSaved && savedPointIds) {
            newMap[screenId] = {
              is_saved: true,
              is_saved_pk: savedPointIds[screenId],
            };
          } else {
            newMap[screenId] = {
              is_saved: false,
              is_saved_pk: null,
            };
          }
        });

        return newMap;
      });
    },
    [],
  );

  useEffect(() => {
    const appPageSlug = appDataOne?.slug; // e.g., "assist-guru-chat-with-ai"
    const appName = appDataOne?.name; // e.g., "AI Chat Bot - Ask Assist Guru"
    const appFullDescription = appDataOne?.description; // The long description
    const appIconUrl = appDataOne?.icon; // App's icon URL
    // For a shorter meta description, we can use shortname or truncate the full description
    const appShortDescription = `See how '${appName}' designs its innovative onboarding flow and paywall strategy. Get insights into its UI/UX on ScreensDesign.`;

    // Only proceed if essential data is available
    if (
      appDataOne &&
      typeof appPageSlug === "string" &&
      appPageSlug.trim() !== "" &&
      typeof appName === "string" &&
      appName.trim() !== "" &&
      typeof appIconUrl === "string" &&
      appIconUrl.trim() !== ""
    ) {
      const calculatedPageUrl = `https://screensdesign.com/apps/${appPageSlug}`; // URL for the app page
      const calculatedPageTitle = `${appName} | app preview video onboarding paywall`;
      const calculatedMetaDescription = appShortDescription; // Use the shorter description for meta

      const calculatedOgTitle = calculatedPageTitle;
      // For OG description, you might use the shorter one, or the start of the longer one
      const calculatedOgDescription = appFullDescription
        ? appFullDescription.substring(0, 297) + "..."
        : appShortDescription;
      const calculatedOgImageUrl = appIconUrl;

      // --- Enhanced helper function to find or create and set meta/link tags ---
      const setTag = (
        tagName: "meta" | "link",
        attributeKey: "name" | "property" | "rel",
        attributeValue: string,
        contentKey: "content" | "href",
        contentValue: string,
      ) => {
        // First try to find existing tag with the correct attribute
        let selector = `${tagName}[${attributeKey}='${attributeValue}']`;
        let tag = document.querySelector<HTMLMetaElement | HTMLLinkElement>(
          selector,
        );

        // For Twitter cards, also check for incorrect 'property' attribute from index.html
        if (
          !tag &&
          attributeKey === "name" &&
          attributeValue.startsWith("twitter:")
        ) {
          const incorrectSelector = `${tagName}[property='${attributeValue}']`;
          tag = document.querySelector<HTMLMetaElement | HTMLLinkElement>(
            incorrectSelector,
          );
          if (tag) {
            // Remove the incorrect attribute and set the correct one
            tag.removeAttribute("property");
            tag.setAttribute("name", attributeValue);
          }
        }

        // For LinkedIn cards, remove them as they don't exist (LinkedIn uses OG tags)
        if (attributeValue.startsWith("linkedin:")) {
          const linkedinTag = document.querySelector(
            `meta[property='${attributeValue}']`,
          );
          if (linkedinTag) {
            linkedinTag.remove();
          }
          return; // Don't create LinkedIn tags
        }

        if (!tag) {
          tag = document.createElement(tagName);
          tag.setAttribute(attributeKey, attributeValue);
          document.head.appendChild(tag);
        }
        tag.setAttribute(contentKey, contentValue);
      };

      // --- Manage Document Title ---
      document.title = calculatedPageTitle;

      // --- Manage Meta Description (standard) ---
      setTag(
        "meta",
        "name",
        "description",
        "content",
        calculatedMetaDescription,
      );

      // --- Manage Canonical Link ---
      setTag("link", "rel", "canonical", "href", calculatedPageUrl);

      // --- Manage Open Graph (OG) Tags ---
      setTag("meta", "property", "og:url", "content", calculatedPageUrl);
      setTag("meta", "property", "og:title", "content", calculatedOgTitle);
      setTag(
        "meta",
        "property",
        "og:description",
        "content",
        calculatedOgDescription,
      );
      setTag("meta", "property", "og:image", "content", calculatedOgImageUrl);
      setTag("meta", "property", "og:type", "content", "website"); // 'website' or 'article' for an app page
      // If there's a specific video shown on this app page, you could consider adding 'og:video' if appDataOne.avs.url is available
      if (appDataOne?.avs?.url) {
        setTag("meta", "property", "og:video", "content", appDataOne.avs.url);
        // You might want to also set og:video:type, og:video:width, og:video:height if known
      }
      setTag("meta", "property", "og:site_name", "content", "ScreensDesign");

      // --- Manage Twitter Card Tags (will fix the incorrect 'property' attributes from index.html) ---
      setTag("meta", "name", "twitter:card", "content", "summary_large_image");
      setTag("meta", "name", "twitter:url", "content", calculatedPageUrl);
      setTag("meta", "name", "twitter:title", "content", calculatedOgTitle);
      setTag(
        "meta",
        "name",
        "twitter:description",
        "content",
        calculatedOgDescription,
      ); // Using the longer OG description for Twitter
      setTag("meta", "name", "twitter:image", "content", calculatedOgImageUrl);
      // If you have a Twitter handle for the site:
      // setTag('meta', 'name', 'twitter:site', 'content', '@YourTwitterHandle');

      // Remove LinkedIn tags (they don't exist - LinkedIn uses OG tags)
      setTag("meta", "property", "linkedin:card", "content", "");
      setTag("meta", "property", "linkedin:url", "content", "");
      setTag("meta", "property", "linkedin:title", "content", "");
      setTag("meta", "property", "linkedin:description", "content", "");
      setTag("meta", "property", "linkedin:image", "content", "");
    }
  }, [appDataOne]); // Re-run this effect when appDataOne changes

  // Check for errors from the hook
  const hasVideoError =
    isErrorDataVideosOne || isErrorDataVideosOneOB || isErrorAppDataOne;

  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 640;

  return state.hasNullItem || hasVideoError ? (
    <div className={styles.noMatches}>
      <img
        src="/img/Illustrations.jpg"
        width="250px"
        height="250px"
        alt="error loading video"
      />
      <h2>App Not Found</h2>
      <p>
        The app you're looking for doesn't exist or has been removed.
        <br />
        Please check the URL or browse our app collection.
      </p>
    </div>
  ) : (
    <>
      {!state.videoReady && !apiDataVideosOneActive ? (
        <div className={styles.loadingContainer}>
          <Spinner width={45} />
        </div>
      ) : (
        <>
          {(apiDataVideosOneOB || apiDataVideosOne) && (
            <div className={styles.videoTypeSwitch}>
              {apiDataVideosOne && apiDataVideosOneOB && (
                <button
                  className={`${styles.switchButton} ${videoType === 1 ? styles.active : ""}`}
                  onClick={() => handleVideoTypeChange(1)}
                >
                  {/* <img src="/img/appVideo.svg" alt="appVideo" /> */}
                  App Replay
                </button>
              )}
              {apiDataVideosOneOB && (
                <button
                  className={`${styles.switchButton} ${videoType === 2 ? styles.active : ""}`}
                  onClick={() => handleVideoTypeChange(2)}
                >
                  {/* <img src="/img/onboardingVideo.svg" alt="onboardingVideo" /> */}
                  Web Onboarding
                </button>
              )}
            </div>
          )}
          <div ref={ref} className={styles.containerRoot}>
            {isMobile && (
              <div className={styles.headerContainerMobile}>
                <Header data={appDataOne} appId={Number(appId)} />
                <VersionsMenu appId={appId} />
              </div>
            )}
            <div className={styles.phoneContainer}>
              {appDataOne && currentVideoData?.video && !isMobile && (
                <div className={styles.headerContainer}>
                  <Header data={appDataOne} appId={Number(appId)} />
                </div>
              )}
              <div className={styles.phoneContainerPhone}>
                <Phone
                  videoRef={videoRef}
                  setVideoReady={setVideoReady}
                  videoReady={state.videoReady}
                  apiDataVideosOne={currentVideoData}
                  setIsTimestampReady={setIsTimestampReady}
                  isTimestampReady={state.isTimestampReady}
                  isModalOpen={state.isModalOpen}
                  inViewLottie={inViewLottie}
                  appDataOne={appDataOne}
                  isActivePoster={isActivePoster}
                  setIsActivePoster={setIsActivePoster}
                  userData={userData}
                  openModal={openModal}
                  handleOpenBillingPage={handleOpenBillingPage}
                  currentOrganization={currentOrganization}
                />
              </div>
              {!isMobile && (
                <div className={styles.versionsMenuContainer}>
                  <VersionsMenu appId={appId} />
                </div>
              )}
            </div>
            <div className={styles.controller}>
              <div className={`${styles.containerButton}`}>
                <div className={styles.containerButtonLeft}>
                  <ButtonPay />
                  {videoType === 1 && !isMobile && (
                    <SavePosition
                      apiDataVideosOne={currentVideoData}
                      isLoading={isCurrentVideoLoading}
                      styleProps=""
                      withBefore={true}
                      setIsActivePoster={setIsActivePoster}
                      apiDataPointsByVideo={apiDataPointsByVideo}
                      onSavePointClick={handleSavePointClick}
                      isPro={userData?.is_pro}
                      selectedTimestamp={state.currentPlayTime.toFixed(0)}
                    />
                  )}
                </div>
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(videoActions.togglePlayPause());
                  }}
                  className={styles.playTimer}
                >
                  <>
                    <div className={styles.playTimer}>
                      <div className={styles.playTimerItem}>
                        <img
                          src={
                            state.playing
                              ? "/img/pauseIconTimer.svg"
                              : "/img/playIconTimer.svg"
                          }
                          alt="TimerIcon"
                        />
                      </div>
                      <p>
                        {secondsToHMS(Number(state.currentPlayTime.toFixed(0)))}
                        <span>/</span>
                        {secondsToHMS(
                          Number(currentVideoData?.duration_seconds),
                        )}
                      </p>
                    </div>
                  </>
                </div>
              </div>
              {!isMobile && (
                <div className={styles.controlsContainer}>
                  {currentVideoData?.video && (
                    <div className={styles.containerVideo}>
                      {apiDataVideosScreensById &&
                        currentVideoData?.video !== null &&
                        appVideoScreens.length > 0 &&
                        videoType !== 2 && (
                          <div>
                            <VideoControls
                              onSeek={onSeek}
                              apiDataVideosOne={currentVideoData}
                              inView={inView}
                              activeFlowRef={activeFlowRef}
                              setMarkers={setMarkers}
                              inViewLottie={inViewLottie}
                              croppedImages={state.croppedImages}
                              apiDataPointsByVideo={apiDataPointsByVideo}
                              apiDataVideosLabelsById={apiDataVideosLabelsById}
                            />
                          </div>
                        )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className={styles.containerButtons}>
            {(videoType === 2 ? webOnboardingData : buttonData).map(
              (item, index) => (
                <ButtonFilter
                  key={index}
                  text={item.text}
                  imgActive={item.imgActive}
                  isActive={index === activeIndex}
                  onClick={() =>
                    dispatch(modalMenuPhoneActions.setActiveIndex(index))
                  }
                  height="38px"
                />
              ),
            )}
          </div>

          {activeIndex === 1 && appStoreScreens.length > 0 && (
            <div className={styles.containerAppStoreScreens}>
              <AppStoreScreensModal
                appStoreScreens={appStoreScreens}
                appStoreImageId={appStoreImageId}
                appId={appId}
                isPro={userData ? userData.is_pro : false}
                refetchDataStore={refetchDataStore}
              />
            </div>
          )}

          {currentVideoData?.video !== null &&
            activeIndex === 0 &&
            videoType === 1 && (
              <div
                className={`${styles.photoCardList} `}
                ref={photoCardListRef}
              >
                <PhotoCardList
                  onSeek={onSeek}
                  appVideoScreens={appVideoScreens}
                  setImagesReady={setImagesReady}
                  imagesReady={state.imagesReady}
                  apiDataVideosOne={currentVideoData}
                  setIsActivePoster={setIsActivePoster}
                  ref={phoneRef}
                  isModalOpenPhotoCard={isModalOpenPhotoCard}
                  handleOpenBillingPage={handleOpenBillingPage}
                  refetchDataScreens={refetchDataScreens}
                />
              </div>
            )}

          {activeIndex === 0 && videoType === 2 && currentVideoData?.video && (
            <div className={styles.containerWebOnboarding}>
              <WebOnboardingVideoPage
                videoData={currentVideoData}
                isOpen={state.isOpen}
                savedScreensMap={savedScreensMap}
              />
            </div>
          )}

          <AdditionalInfo
            data={appDataOne}
            isHidden={isHidden || false}
            appId={Number(appId)}
            hiddenAppItemId={hiddenAppItem?.id}
          />
        </>
      )}
      {state.isModalOpen && (
        <div className={styles.modalOverlay} onClick={handleOverlayClick} />
      )}
      {selectedScreens.length > 0 && (
        <SelectedScreensPopup
          type={getScreenTypeForPopup()}
          setSimilarScreenResults={setAppVideoScreens}
          setAppStoreScreens={setAppStoreScreens}
          updateScreensSavedStatus={updateScreensSavedStatus}
        />
      )}
    </>
  );
};

export { AppVideoView };
